import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Port } from '../Map/AISMapContext';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  convertRouteValuesToBackend,
  mutateRouteFormSchema,
  MutateRouteFormValues,
} from './Mutations/routeForm';
import { mutateCallbacks } from '../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateTrack } from '@ais-ui/api';
import { useToast } from '@ais-ui/ui-core';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface RoutesContextProps {
  port: Port | null;
  setPort: React.Dispatch<React.SetStateAction<Port | null>>;
  destinationType: string | null;
  shape: GeoJson | null;
  setShape: React.Dispatch<React.SetStateAction<GeoJson | null>>;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  onCreateRoute: (data: MutateRouteFormValues) => void;
  form: UseFormReturn<any>;
}

const RoutesContext = createContext<RoutesContextProps | null>(null);

export const RoutesProvider: React.FC<ShapeProviderProps> = ({ children }) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { toast } = useToast();
  const { messages } = useLocalization();
  const { mutate } = useCreateTrack();

  const [port, setPort] = useState<Port | null>(null);
  const [shape, setShape] = useState<GeoJson | null>(null);

  useEffect(() => {
    if (port) {
      setShape(null);
    }
  }, [port]);

  useEffect(() => {
    if (shape) {
      setPort(null);
    }
  }, [shape]);

  const form = useForm<MutateRouteFormValues>({
    resolver: zodResolver(mutateRouteFormSchema),
    defaultValues: {
      destinationShapeId: '',
      destinationPortId: '',
    },
  });

  useEffect(() => {
    // Reset form when dialog closes
    if (!showDialog) {
      form.clearErrors();
    }
  }, [showDialog, form]);

  const destinationType = useMemo(() => {
    if (port) {
      form.setValue('destinationShapeId', '');
      return 'port';
    } else if (shape) {
      form.setValue('destinationPortId', '');
      return 'shape';
    }
    return null;
  }, [form, port, shape]);

  const onCreateRoute = async (data: MutateRouteFormValues) => {
    const backendValues = convertRouteValuesToBackend(data);

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'create',
      'route',
      setIsSubmitting,
      setShowDialog,
      {
        409: 'Route with ship and destination already exists.',
      }
    );

    mutate(backendValues, callBacks);
  };

  const value = {
    port,
    setPort,
    destinationType,
    shape,
    setShape,
    showDialog,
    setShowDialog,
    isSubmitting,
    onCreateRoute,
    form,
  };

  return (
    <RoutesContext.Provider value={value}>{children}</RoutesContext.Provider>
  );
};

export const useRoutes = () => {
  const context = useContext(RoutesContext);
  if (!context) {
    throw new Error('useRoutes must be used within a RoutesProvider');
  }
  return context;
};

interface ShapeProviderProps {
  children: ReactNode;
}
